import { useQuery, useQueries, useQueryClient } from 'react-query';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaPetsV1Service } from '../service';
import { BulkFetchPetsByServiceableIdsIO } from '../types';

export const getBulkFetchPetsByServiceableIdsQueryKey = (req: BulkFetchPetsByServiceableIdsIO['input']) =>
  ['PetsV1', 'BulkFetchPetsByServiceableIds', req] as const;
type QueryKey = ReturnType<typeof getBulkFetchPetsByServiceableIdsQueryKey>;

/**
 * A hook that returns a query for the `BulkFetchPetsByServiceableIds` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useBulkFetchPetsByServiceableIdsQuery = <E = unknown, D = BulkFetchPetsByServiceableIdsIO['output']>(
  req: BulkFetchPetsByServiceableIdsIO['input'],
  options?: LimitedSchemaQueryOptions<BulkFetchPetsByServiceableIdsIO, E, D, QueryKey>,
  httpOptions?: Options
) =>
  useQuery<BulkFetchPetsByServiceableIdsIO['output'], E, D, QueryKey>({
    queryKey: getBulkFetchPetsByServiceableIdsQueryKey(req),
    queryFn: () => SchemaPetsV1Service.BulkFetchPetsByServiceableIds(req, httpOptions),
    ...options,
  });

/**
 * A hook that returns an array of queries for the `BulkFetchPetsByServiceableIds` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useBulkFetchPetsByServiceableIdsQueries = <E = unknown, D = BulkFetchPetsByServiceableIdsIO['output']>(
  argsArr: {
    req: BulkFetchPetsByServiceableIdsIO['input'];
    options?: LimitedSchemaQueryOptions<BulkFetchPetsByServiceableIdsIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) =>
  useQueries(
    argsArr.map(({ req, options, httpOptions }) => ({
      queryKey: getBulkFetchPetsByServiceableIdsQueryKey(req),
      queryFn: () => SchemaPetsV1Service.BulkFetchPetsByServiceableIds(req, httpOptions),
      ...options,
    }))
  ) as ReturnType<typeof useBulkFetchPetsByServiceableIdsQuery<E, D>>[];

/**
 * A hook that returns a function to fetch the query for the `BulkFetchPetsByServiceableIds` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useFetchBulkFetchPetsByServiceableIds = () => {
  const queryClient = useQueryClient();

  const fetchBulkFetchPetsByServiceableIds = async <E = unknown, D = BulkFetchPetsByServiceableIdsIO['output']>(
    req: BulkFetchPetsByServiceableIdsIO['input'],
    options?: LimitedSchemaFetchQueryOptions<BulkFetchPetsByServiceableIdsIO, E, D, QueryKey>,
    httpOptions?: Options
  ) => {
    const response = await queryClient.fetchQuery<BulkFetchPetsByServiceableIdsIO['output'], E, D, QueryKey>(
      getBulkFetchPetsByServiceableIdsQueryKey(req),
      () => SchemaPetsV1Service.BulkFetchPetsByServiceableIds(req, httpOptions),
      options
    );

    return response;
  };

  return fetchBulkFetchPetsByServiceableIds;
};
